import { type CanActivateFn, RedirectCommand, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  FeatureAccessRightsService,
  FeatureRequirement
} from '@app/shared/features/access-rights/services/feature-access-rights.service';
import { ROUTES } from '@app/app.routes-constants';
import { tap } from 'rxjs';

/**
 * Creates a route guard that checks if the user has access to a specific feature.
 *
 * @param requiredFeature - The feature requirement to check access for
 * @param redirectTo - The route to redirect to if access is denied.
 *   **Default**: `ROUTES.ERROR_PAGE_STATUS.UNAUTHORIZED`
 *   Can be:
 *   - A single route string (e.g., '/dashboard')
 *   - An array of route segments (e.g., ['/users', userId])
 *   - A pre-configured UrlTree for complex navigation
 * @param options - Optional configuration for the guard behavior
 * @param options.skipLocationChange - If true, performs navigation without updating the browser URL
 *   to the redirect target, while preserving the target route URL (when preserveTargetUrlOnSoftRedirect is true).
 * @param options.preserveTargetUrlOnSoftRedirect - If true (default), ensures the browser URL is updated
 *   to the target route before performing the redirect. This option only works when combined with
 *   `skipLocationChange: true`.
 *
 *   **Default**: `true`
 *
 *   **Problem Solved**: When navigating from one route to another that lacks access, without this option
 *   the URL remains at the previous route instead of updating to the intended target route. This prevents
 *   users from refreshing/bookmarking the correct URL. With `preserveTargetUrlOnSoftRedirect` enabled
 *   (default behavior), the URL shows the target route (e.g., '/todo') while displaying the unauthorized
 *   content, allowing users to reload or bookmark the intended destination.
 *
 *   **Example Scenario**: User is on '/cancelled' and clicks a link to '/todo' but lacks TODO access.
 *   - With `preserveTargetUrlOnSoftRedirect: true` (default): URL updates to '/todo', unauthorized content shows
 *   - With `preserveTargetUrlOnSoftRedirect: false`: URL stays '/cancelled', unauthorized content shows
 *
 * @returns A CanActivateFn guard function that:
 *   - Returns `true` if the user has access to the feature
 *   - Returns a `RedirectCommand` with skipLocationChange if access is denied and skipLocationChange is enabled
 *   - Returns a `UrlTree` for standard redirect if access is denied (redirects to `ROUTES.ERROR_PAGE_STATUS.UNAUTHORIZED` by default)
 *
 * @example
 * ```typescript
 * // Simple usage with default redirect (ROUTES.ERROR_PAGE_STATUS.UNAUTHORIZED)
 * {
 *   path: 'admin',
 *   canActivate: [featureAccessCanActivateGuard({ requiredFeature: 'ADMIN_ACCESS' })]
 * }
 *
 * // With custom redirect
 * {
 *   path: 'reports',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: 'REPORTS',
 *     redirectTo: '/dashboard'
 *   })]
 * }
 *
 * // With skipLocationChange - shows unauthorized page with target URL preserved (default behavior)
 * // URL will show '/todo', content shows unauthorized page
 * {
 *   path: 'todo',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: FeaturesEnum.TodoTransactions,
 *     options: { skipLocationChange: true }
 *     // preserveTargetUrlOnSoftRedirect defaults to true
 *   })]
 * }
 *
 * // Explicitly disable URL preservation (keep previous route URL)
 * {
 *   path: 'sensitive',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: 'SENSITIVE',
 *     redirectTo: '/home',
 *     options: {
 *       skipLocationChange: true,
 *       preserveTargetUrlOnSoftRedirect: false  // URL stays at previous route
 *     }
 *   })]
 * }
 *
 * // With pre-configured UrlTree for complex navigation
 * const urlTree = router.createUrlTree(['/users', userId], {
 *   queryParams: { tab: 'details' }
 * });
 * {
 *   path: 'profile',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: 'PROFILE_ACCESS',
 *     redirectTo: urlTree
 *   })]
 * }
 *
 * // With { anyOf: [...] }: User needs 'EDITOR' OR 'PUBLISHER' access
 * {
 *   path: 'edit-content',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: { anyOf: ['EDITOR', 'PUBLISHER'] }
 *   })]
 * }
 *
 * // With { allOf: [...] }: User needs BOTH 'VIEW_DASHBOARD' AND 'FINANCIAL_REPORTS' access
 * {
 *   path: 'financial-data',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: { allOf: ['VIEW_DASHBOARD', 'FINANCIAL_REPORTS'] },
 *     redirectTo: '/home'
 *   })]
 * }
 *
 * // With { noneOf: [...] }: User needs none of the accesses
 * {
 *   path: 'financial-data',
 *   canActivate: [featureAccessCanActivateGuard({
 *     requiredFeature: { noneOf: ['VIEW_DASHBOARD', 'FINANCIAL_REPORTS'] },
 *     redirectTo: '/home'
 *   })]
 * }
 * ```
 */
export function featureAccessCanActivateGuard({
  requiredFeature,
  redirectTo,
  options
}: {
  /**
   * @example
   * ```typescript
   * // Single feature:
   * requiredFeature: FeatureRequirement,
   *
   * // Multi feature:
   * requiredFeature: { allOf: ['VIEW_DASHBOARD', 'FINANCIAL_REPORTS'] },
   * requiredFeature: { anyOf: ['EDITOR', 'PUBLISHER'] }
   * requiredFeature: { noneOf: ['EDITOR', 'PUBLISHER'] }
   *
   * ```
   * */
  requiredFeature: FeatureRequirement,
  redirectTo?: string | string[] | UrlTree,
  options?: {
    skipLocationChange?: boolean,
    preserveTargetUrlOnSoftRedirect?: boolean
  }
}): CanActivateFn {
  return (route, state) => {
    // DI
    const access = inject(FeatureAccessRightsService);
    const router = inject(Router);

    const defaultOptions = {
      preserveTargetUrlOnSoftRedirect: options?.preserveTargetUrlOnSoftRedirect ?? true,
      redirectTo: redirectTo ?? ROUTES.ERROR_PAGE_STATUS.UNAUTHORIZED
    };

    return access.hasFeatureAccess$(requiredFeature).pipe(
      tap(hasAccess => {
        // If access denied and we need to update URL first
        if (!hasAccess && defaultOptions.preserveTargetUrlOnSoftRedirect && options?.skipLocationChange) {
          // Force URL update to the target route before redirecting
          const currentUrl = state.url;
          if (router.url !== currentUrl) {
            window.history.pushState(null, '', currentUrl);
          }
        }
      }),
      map(hasAccess => {
        // User has access - allow navigation
        if (hasAccess) {
          return true;
        }

        // Create or use the provided URL tree
        // Defaults to ROUTES.ERROR_PAGE_STATUS.UNAUTHORIZED
        const urlTree = defaultOptions.redirectTo instanceof UrlTree
          ? defaultOptions.redirectTo
          : router.createUrlTree(Array.isArray(defaultOptions.redirectTo) ? defaultOptions.redirectTo : [defaultOptions.redirectTo]);

        // Use RedirectCommand to skipLocationChange
        if (options?.skipLocationChange) {
          return new RedirectCommand(urlTree, { skipLocationChange: true });
        }

        return urlTree;
      })
    );
  };
}
